/**
 * Client-side manifest loader.
 *
 * The backend does not currently return image URLs or the full catalogue of
 * ~4700 SKUs / 441 brands. Both are shipped as static JSON in `public/` and
 * fetched once, cached, and used to:
 *
 *   1) resolve product image URLs (`/product-images/<folder>/<file>.jpg`) from
 *      a product slug or id (`resolveProductImage`),
 *   2) augment PLPs with catalogue rows that don't (yet) exist in the DB
 *      (`getImageProducts`, `getBrandsCatalog`, `getCategoriesCatalog`).
 *
 * The old UI in `apps/web/` reads the same manifests at build time.
 */

const IMG_URL = "/product-image-manifest.json";
const CAT_URL = "/catalog-manifest.json";

let imgManifestPromise = null;
let catalogManifestPromise = null;

// In-memory cache of the parsed manifests. Populated after the fetch resolves.
let imgManifest = null;
let catalogManifest = null;

// Also expose a slug→product map derived from imageProducts so lookups are O(1).
let imageProductsBySlug = null;
let imageProductsByCategory = null;
let imageProductsByBrand = null;

async function fetchJson(url) {
  const res = await fetch(url, { cache: "force-cache" });
  if (!res.ok) throw new Error(`Manifest fetch failed: ${url} (${res.status})`);
  return res.json();
}

export function loadImageManifest() {
  if (imgManifestPromise) return imgManifestPromise;
  imgManifestPromise = fetchJson(IMG_URL)
    .then((m) => {
      imgManifest = m;
      // Build per-category / per-brand indexes lazily on first read to keep
      // the initial fetch fast (5MB parse is already substantial).
      return m;
    })
    .catch((err) => {
      console.warn("[manifest] image manifest unavailable", err);
      imgManifest = { images: {}, products: {}, imageProducts: [], imageCategories: [] };
      return imgManifest;
    });
  return imgManifestPromise;
}

export function loadCatalogManifest() {
  if (catalogManifestPromise) return catalogManifestPromise;
  catalogManifestPromise = fetchJson(CAT_URL)
    .then((m) => {
      catalogManifest = m;
      return m;
    })
    .catch((err) => {
      console.warn("[manifest] catalog manifest unavailable", err);
      catalogManifest = { brands: [], categories: [] };
      return catalogManifest;
    });
  return catalogManifestPromise;
}

// Kick both fetches on module import (safe: happens once per SPA session).
if (typeof window !== "undefined") {
  loadImageManifest();
  loadCatalogManifest();
}

// ------------------------------- resolvers -------------------------------

/** Sync — returns the cached manifest or null if not yet loaded. */
export function imageManifestSync() {
  return imgManifest;
}

export function catalogManifestSync() {
  return catalogManifest;
}

function ensureImageProductIndexes() {
  if (!imgManifest || imageProductsBySlug) return;
  imageProductsBySlug = new Map();
  imageProductsByCategory = new Map();
  imageProductsByBrand = new Map();
  for (const p of imgManifest.imageProducts || []) {
    if (p.slug && !imageProductsBySlug.has(p.slug)) imageProductsBySlug.set(p.slug, p);
    const cats = p.categorySlugs || (p.primaryCategorySlug ? [p.primaryCategorySlug] : []);
    for (const c of cats) {
      if (!imageProductsByCategory.has(c)) imageProductsByCategory.set(c, []);
      imageProductsByCategory.get(c).push(p);
    }
    if (p.brandId) {
      if (!imageProductsByBrand.has(p.brandId)) imageProductsByBrand.set(p.brandId, []);
      imageProductsByBrand.get(p.brandId).push(p);
    }
  }
}

/**
 * Resolve a product image URL from the manifest.
 * Tries: slug → productId → fuzzy token match on the product name.
 * Returns a fully-qualified /product-images/... path or null.
 */
export function resolveProductImage({ slug, id, productId, name, brand }) {
  if (!imgManifest) return null;
  const images = imgManifest.images || {};
  const products = imgManifest.products || {};

  const trySlugs = [slug, id, productId]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());

  for (const s of trySlugs) {
    if (images[s] && images[s].length) return images[s][0];
    if (products[s] && products[s].length) return products[s][0];
  }

  // Fuzzy fallback: token-based lookup on the product name against the
  // manifest's slug keys. Cheap when we already have an ensured index.
  if (name) {
    const targetTokens = tokenize(`${brand || ""} ${name}`);
    if (targetTokens.size < 2) return null;
    let bestScore = 0;
    let bestUrl = null;
    for (const [key, urls] of Object.entries(images)) {
      const keyTokens = tokenize(key);
      const shared = intersectSize(targetTokens, keyTokens);
      if (shared < 2) continue;
      const score = shared / (keyTokens.size + targetTokens.size - shared);
      if (score > bestScore) {
        bestScore = score;
        bestUrl = urls[0];
        if (score > 0.7) break; // good enough
      }
    }
    return bestScore >= 0.3 ? bestUrl : null;
  }
  return null;
}

const STOPWORDS = new Set([
  "the","and","with","for","of","set","kit","pack","box","ml","mg","gm","kg","oz","cm","mm","pc","pcs","new","plus","pro","std","inc","ltd"
]);
function tokenize(s) {
  const out = new Set();
  for (const t of String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").split(/\s+/)) {
    if (t.length >= 3 && !STOPWORDS.has(t)) out.add(t);
  }
  return out;
}
function intersectSize(a, b) {
  let n = 0;
  for (const x of a) if (b.has(x)) n += 1;
  return n;
}

// -------- Catalog augmentation (imageProducts fill-in for empty PLPs) --------

export function getImageProducts({ categorySlug, brandId, q, limit = 40 } = {}) {
  if (!imgManifest) return [];
  ensureImageProductIndexes();
  let rows;
  if (categorySlug && imageProductsByCategory.has(categorySlug)) {
    rows = imageProductsByCategory.get(categorySlug);
  } else if (brandId && imageProductsByBrand.has(brandId)) {
    rows = imageProductsByBrand.get(brandId);
  } else {
    rows = imgManifest.imageProducts || [];
  }
  if (q) {
    const term = q.toLowerCase();
    rows = rows.filter((p) => (p.name || "").toLowerCase().includes(term));
  }
  return rows.slice(0, limit);
}

export function getBrandsCatalog() {
  if (!catalogManifest) return [];
  return catalogManifest.brands || [];
}

export function getCategoriesCatalog() {
  if (!catalogManifest) return [];
  return catalogManifest.categories || [];
}

export function getImageCategories() {
  if (!imgManifest) return [];
  return imgManifest.imageCategories || [];
}

/** Fetch a specific image-derived product by slug (for PDP fallback). */
export function getImageProductBySlug(slug) {
  if (!imgManifest) return null;
  ensureImageProductIndexes();
  return imageProductsBySlug?.get(slug) || null;
}
